/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * Single source of truth for writing the CSRF cookie.
 *
 * `csrf_token` is a double-submit token the SPA must be able to read, so it is
 * intentionally NOT HttpOnly (VAPT-2026-TRQ-05). It MUST, however, carry the
 * `Secure` flag on HTTPS so it is never transmitted over plaintext
 * (VAPT-2026-TRQ-04). Centralising the write here keeps every call site
 * consistent: the logic was previously duplicated and one copy
 * (`authenticatedClient`) omitted `Secure`, overwriting the backend's Secure
 * cookie with a non-Secure one on every authenticated response.
 */
export function setCsrfCookie(token: string): void {
  const isSecure = window.location.protocol === "https:";
  // URL-encode the value so a reserved character (`;`, `,`, whitespace, …) can
  // never break the cookie's structure. Backend tokens are url-safe today, so
  // this is a no-op for them, but the readers decode (see getCsrfToken in
  // core.ts), keeping write/read symmetric if the token format ever changes.
  const value = encodeURIComponent(token);
  document.cookie = `csrf_token=${value}; path=/; SameSite=Strict${isSecure ? "; Secure" : ""}`;
}
